/**
 * Dice expression parser and roller using crypto.getRandomValues for fairness.
 *
 * Supports standard D&D dice notation:
 *   - NdX       → roll N dice with X faces (e.g. 2d6)
 *   - dX        → shorthand for 1dX (e.g. d20)
 *   - NdX+M     → roll + flat modifier (e.g. 1d20+5)
 *   - NdX-M     → roll - flat modifier (e.g. 2d8-1)
 *   - Multiple groups: 2d6+1d4+3  (additive only)
 *   - NdXkhK / NdXklK → keep the highest / lowest K dice of the group
 *     (2d20kh1 = advantage, 2d20kl1 = disadvantage). A bare kh/kl keeps 1.
 *
 * The parser is intentionally simple — no parentheses, no multiplication.
 * Keep-highest/lowest exists because advantage is a per-check need at the
 * table (dev handoff 2026-09-21, `vtt-roll-controls-discoverability`) and
 * the feed must be able to show BOTH dice with the kept one obvious.
 */

/** A single parsed dice group: NdX, optionally keeping only some dice. */
export interface DiceGroup {
  count: number;
  faces: number;
  /** Keep only the highest/lowest `keepCount` dice of this group toward the total. */
  keep?: 'highest' | 'lowest';
  keepCount?: number;
}

/**
 * Roll mode for a d20 check — the wire field the roll feed reads
 * (`RollRequest.mode` / `LogEntryPayload.mode`; C# RollModes). 'normal' is the
 * default and is what an omitted field means.
 */
export type RollMode = 'normal' | 'advantage' | 'disadvantage';

export const ROLL_MODES: readonly RollMode[] = ['normal', 'advantage', 'disadvantage'];

/** Short badge text for a mode; '' for normal. */
export function rollModeBadge(mode: RollMode | null | undefined): string {
  return mode === 'advantage' ? 'ADV' : mode === 'disadvantage' ? 'DIS' : '';
}

/**
 * The d20 check expression for a modifier + mode: "1d20+5", "2d20kh1+5"
 * (advantage) or "2d20kl1+5" (disadvantage). `extra` is an optional
 * already-signed bonus expression appended verbatim (e.g. "+1d4" for Bless).
 */
export function d20CheckExpression(modifier: number, mode: RollMode = 'normal', extra = ''): string {
  const die = mode === 'advantage' ? '2d20kh1' : mode === 'disadvantage' ? '2d20kl1' : '1d20';
  const mod = modifier === 0 ? '' : modifier > 0 ? `+${modifier}` : `${modifier}`;
  return `${die}${mod}${extra}`;
}

/** Infer the mode from an expression's first d20 group (the inverse of d20CheckExpression). */
export function rollModeFromExpression(expression: string): RollMode {
  let parsed: ParsedExpression;
  try {
    parsed = parseDiceExpression(expression);
  } catch {
    return 'normal';
  }
  const g = parsed.groups.find((x) => x.faces === 20);
  if (!g || !g.keep || g.count < 2) return 'normal';
  return g.keep === 'highest' ? 'advantage' : 'disadvantage';
}

export interface ParsedExpression {
  groups: DiceGroup[];
  modifier: number;
}

/**
 * Parse a dice expression string into structured groups + modifier.
 * Throws on invalid input.
 *
 * Examples:
 *   "2d6+3"    → groups: [{count:2, faces:6}], modifier: 3
 *   "1d20+1d4" → groups: [{count:1, faces:20}, {count:1, faces:4}], modifier: 0
 *   "d8-1"     → groups: [{count:1, faces:8}], modifier: -1
 *   "3d6"      → groups: [{count:3, faces:6}], modifier: 0
 */
export function parseDiceExpression(input: string): ParsedExpression {
  const expr = input.replace(/\s+/g, '').toLowerCase();
  if (!expr) throw new Error('Empty dice expression');

  const groups: DiceGroup[] = [];
  let modifier = 0;

  // Split on + and - while keeping the sign as a delimiter.
  // "2d6+3-1d4+2" → ["2d6", "+3", "-1d4", "+2"]
  const tokens = expr.match(/[+-]?[^+-]+/g);
  if (!tokens) throw new Error(`Invalid dice expression: ${input}`);

  for (const token of tokens) {
    const diceMatch = token.match(/^([+-]?)(\d*)d(\d+)(?:k([hl])(\d*))?$/);
    if (diceMatch) {
      const sign = diceMatch[1] === '-' ? -1 : 1;
      const count = diceMatch[2] ? parseInt(diceMatch[2], 10) : 1;
      const faces = parseInt(diceMatch[3], 10);
      if (count <= 0 || count > 100) throw new Error(`Die count out of range: ${count}`);
      if (faces <= 0 || faces > 1000) throw new Error(`Die faces out of range: ${faces}`);
      // Negative dice groups (e.g. -1d4) aren't standard D&D notation but are
      // technically parseable; for now we only support positive groups.
      if (sign < 0) throw new Error('Negative dice groups are not supported');
      const group: DiceGroup = { count, faces };
      if (diceMatch[4]) {
        const keepCount = diceMatch[5] ? parseInt(diceMatch[5], 10) : 1;
        if (keepCount <= 0 || keepCount > count) {
          throw new Error(`Keep count out of range: ${keepCount} of ${count}`);
        }
        group.keep = diceMatch[4] === 'h' ? 'highest' : 'lowest';
        group.keepCount = keepCount;
      }
      groups.push(group);
      continue;
    }

    const modMatch = token.match(/^([+-]?\d+)$/);
    if (modMatch) {
      modifier += parseInt(modMatch[1], 10);
      continue;
    }

    throw new Error(`Unrecognised token in dice expression: ${token}`);
  }

  if (groups.length === 0) {
    throw new Error(`No dice in expression: ${input}`);
  }

  return { groups, modifier };
}

/**
 * Roll a single die with the given number of faces using crypto.getRandomValues.
 * Returns a value in [1, faces].
 */
function rollOne(faces: number): number {
  // Use rejection sampling to avoid modulo bias. We generate a 32-bit
  // unsigned integer and reject values that fall in the biased tail.
  const max = Math.floor(0x100000000 / faces) * faces;
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0] < max) {
      return (buf[0] % faces) + 1;
    }
  }
}

export interface RollResult {
  /** The original expression string. */
  expression: string;
  /** Individual die results in roll order (does not include modifier). ALL dice, kept or not. */
  results: number[];
  /** Sum of the KEPT dice + modifier. */
  total: number;
  /** Parsed modifier (0 if none). */
  modifier: number;
  /** Indices into `results` that were dropped by a kh/kl group (absent when nothing was dropped). */
  dropped?: number[];
}

/**
 * Indices (into a group's slice of results) that a keep rule drops. Ties are
 * resolved by position so the outcome is deterministic.
 */
export function droppedIndices(values: number[], keep: DiceGroup['keep'], keepCount: number): number[] {
  if (!keep || keepCount >= values.length) return [];
  const order = values
    .map((v, i) => ({ v, i }))
    .sort((a, b) => (keep === 'highest' ? b.v - a.v : a.v - b.v) || a.i - b.i);
  return order
    .slice(keepCount)
    .map((x) => x.i)
    .sort((a, b) => a - b);
}

/** Total a set of rolled results against their parsed expression (kept dice + modifier). */
export function totalResults(parsed: ParsedExpression, results: number[]): { total: number; dropped: number[] } {
  let cursor = 0;
  let sum = 0;
  const dropped: number[] = [];
  for (const group of parsed.groups) {
    const slice = results.slice(cursor, cursor + group.count);
    const drop = droppedIndices(slice, group.keep, group.keepCount ?? slice.length);
    for (let i = 0; i < slice.length; i++) {
      if (drop.includes(i)) dropped.push(cursor + i);
      else sum += slice[i];
    }
    cursor += group.count;
  }
  return { total: sum + parsed.modifier, dropped };
}

/**
 * Parse an expression, roll all the dice, and return the full result.
 */
export function rollDice(expression: string): RollResult {
  const parsed = parseDiceExpression(expression);
  const results: number[] = [];

  for (const group of parsed.groups) {
    for (let i = 0; i < group.count; i++) {
      results.push(rollOne(group.faces));
    }
  }

  const { total, dropped } = totalResults(parsed, results);
  return {
    expression,
    results,
    total,
    modifier: parsed.modifier,
    ...(dropped.length > 0 ? { dropped } : {}),
  };
}

/**
 * Format a roll result into a human-readable string.
 * e.g. "2d6+3 → [4, 2] +3 = 9"
 */
export function formatRoll(roll: RollResult): string {
  const dropped = new Set(roll.dropped ?? []);
  const dice = `[${roll.results.map((r, i) => (dropped.has(i) ? `(${r})` : `${r}`)).join(', ')}]`;
  const mod = roll.modifier !== 0
    ? ` ${roll.modifier > 0 ? '+' : ''}${roll.modifier}`
    : '';
  return `${roll.expression} → ${dice}${mod} = ${roll.total}`;
}
